import { inPlanningPeriod, planningYearPeriod } from "./planning-period";

export const PROJECT_PHASES = ["ZR", "SR", "Inv", "Kons", "Einz", "Prep"];
export const DISPOSITION_LABELS = {
  flex: "Termin flexibel planbar",
  "fx-bü": "Termin fixiert, findet inhouse im Büro statt",
  "fx-ext": "Termin fixiert, findet extern beim Kunden statt",
  nfx: "Termin noch nicht fixiert",
  res: "Termin provisorisch reserviert",
  mehrere: "Mehrere Dispositionen am nächsten Termin vorhanden",
};
export const PROJECT_SORTS = [
  "name",
  "abacusNr",
  "gruppe",
  "uid",
  "mandatsleitung",
  "phasen",
  "disposition",
  "budget",
  "geplant",
  "kommentar",
];
const collator = new Intl.Collator("de-CH", {
  numeric: true,
  sensitivity: "base",
});
export function projectSnapshots(entries, now = new Date(), references = {}, planningYear) {
  const period = planningYearPeriod(planningYear ?? (now.getUTCMonth() >= 9 ? now.getUTCFullYear() : now.getUTCFullYear() - 1));
  const phaseCodes = Object.fromEntries(
    (references.phases || []).map((item) => [item.id, item.code || item.name]),
  );
  const dispositionCodes = Object.fromEntries(
    (references.dispositions || []).map((item) => [
      item.id,
      item.code || item.name,
    ]),
  );
  const grouped = {};
  for (const entry of (entries || []).filter((entry) => inPlanningPeriod(entry, period)))
    (grouped[entry.mandatId] ??= []).push(entry);
  return Object.fromEntries(
    Object.entries(grouped).map(([id, list]) => {
      const ordered = [...list].sort((a, b) => a.datum.localeCompare(b.datum));
      const upcoming = ordered.filter(
        (x) => new Date(`${x.datum}T23:59:59Z`) >= now,
      );
      const relevant = upcoming.length ? upcoming : ordered;
      const nextDate = relevant[0]?.datum;
      const dispositions = [
        ...new Set(
          relevant
            .filter((x) => x.datum === nextDate)
            .map(
              (x) =>
                dispositionCodes[x.dispositionsstatusId] ||
                x.dispositionsstatusId,
            )
            .filter(Boolean),
        ),
      ];
      return [
        id,
        {
          plannedHours: ordered.reduce(
            (s, x) => s + (Number(x.stunden) || 0),
            0,
          ),
          phases: [
            ...new Set(
              ordered
                .map((x) => phaseCodes[x.phaseId] || x.phaseId)
                .filter(Boolean),
            ),
          ],
          nextDate,
          disposition:
            dispositions.length > 1 ? "mehrere" : dispositions[0] || "nfx",
        },
      ];
    }),
  );
}
export function decorateProjects(
  projects,
  entries,
  employees,
  now,
  references,
  planningYear,
) {
  const snapshots = projectSnapshots(entries, now, references, planningYear),
    employeeById = Object.fromEntries((employees || []).map((x) => [x.id, x]));
  return (projects || []).map((project) => ({
    ...project,
    typ: project.typ || "Mandat",
    snapshot: snapshots[project.id] || {
      plannedHours: 0,
      phases: [],
      nextDate: null,
      disposition: "nfx",
    },
    leaderName:
      employeeById[project.mandatsleitungId]?.name ||
      project.mandatsleitungId ||
      "–",
    leaderAbbreviation: employeeById[project.mandatsleitungId]?.kuerzel || "",
  }));
}
export function filterProjects(rows, filters = {}) {
  const q = (filters.q || "").trim().toLocaleLowerCase("de-CH");
  return rows.filter((row) => {
    // Keep the visible search aligned with the prototype. Status, leadership,
    // phases and comments have their own controls (or are not search filters).
    const searchable = [row.name, row.abacusNr, row.uid, row.gruppe]
      .join(" ")
      .toLocaleLowerCase("de-CH");
    return (
      (!q || searchable.includes(q)) &&
      (!filters.phaseId || row.snapshot.phases.includes(filters.phaseId)) &&
      (!filters.status || row.status === filters.status) &&
      (!filters.mandatsleitungId ||
        row.mandatsleitungId === filters.mandatsleitungId) &&
      (!filters.gruppe || row.gruppe === filters.gruppe) &&
      (!filters.disposition ||
        row.snapshot.disposition === filters.disposition) &&
      (!filters.ohnePlanung ||
        (row.status === "aktiv" && row.snapshot.plannedHours === 0))
    );
  });
}
export function projectSortValue(row, key) {
  return (
    {
      name: row.name,
      abacusNr: row.abacusNr,
      typ: row.typ,
      gruppe: row.gruppe,
      uid: row.uid,
      mandatsleitung: row.leaderName,
      phasen: row.snapshot.phases.join(", "),
      disposition: row.snapshot.disposition,
      budget: Number(row.budget) || 0,
      geplant: row.snapshot.plannedHours,
      kommentar: row.kommentar,
    }[key] ?? ""
  );
}
export function sortProjects(rows, key = "name", direction = "asc") {
  const sign = direction === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = projectSortValue(a, key),
      bv = projectSortValue(b, key);
    const result =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : collator.compare(String(av || ""), String(bv || ""));
    return result * sign || collator.compare(a.id, b.id);
  });
}
export function projectFilterFromSearch(search = "") {
  const p = new URLSearchParams(search);
  return {
    q: p.get("q") || "",
    phaseId: p.get("phaseId") || "",
    status: p.get("status") || "",
    disposition: p.get("disposition") || "",
    mandatsleitungId: p.get("mandatsleitungId") || "",
    gruppe: p.get("gruppe") || "",
    ohnePlanung: p.get("ohnePlanung") === "1",
    view: p.get("view") === "table" ? "table" : "cards",
    sort: PROJECT_SORTS.includes(p.get("sort")) ? p.get("sort") : "name",
    direction: p.get("direction") === "desc" ? "desc" : "asc",
    planningYear: /^\d{4}$/.test(p.get("planningYear") || "") ? Number(p.get("planningYear")) : undefined,
  };
}
export function projectFilterSearch(filters) {
  const p = new URLSearchParams();
  for (const key of [
    "q",
    "phaseId",
    "status",
    "disposition",
    "mandatsleitungId",
    "gruppe",
  ])
    if (filters[key]) p.set(key, filters[key]);
  if (filters.ohnePlanung) p.set("ohnePlanung", "1");
  if (filters.view === "table") p.set("view", "table");
  if (filters.sort && filters.sort !== "name") p.set("sort", filters.sort);
  if (filters.direction === "desc") p.set("direction", "desc");
  if (filters.planningYear) p.set("planningYear", String(filters.planningYear));
  return p.toString();
}
